import { LightningElement, api } from 'lwc';

import { API_BASE_URL } from '../../../utils/awsUtils';
import { animateCopy } from '../../../utils/iconMapper';


function formatCurrencyFromCents(cents, currency = 'USD') {
    const amount = Number(cents);

    if (!Number.isFinite(amount)) {
        return '$0.00';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: String(currency || 'USD').toUpperCase()
    }).format(amount / 100);
}

function formatDate(value) {
    if (!value) return 'Not provided';

    const date = new Date(`${value}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    }).format(date);
}

function compactJoin(values, separator = ', ') {
    return values.filter(Boolean).join(separator);
}

export default class GetWetBookingSuccess extends LightningElement {
    _sessionId = null;
    _session = null;
    _listing = null;
    _isLoading = false;
    _errorMessage = null;

    @api
    set sessionId(value) {
        this._sessionId = value || null;
        this.loadCheckoutSession();
    }

    get sessionId() {
        return this._sessionId;
    }

    connectedCallback() {
        this.loadCheckoutSession();
    }

    async loadCheckoutSession() {
        if (!this._sessionId || this._isLoading) return;

        this._isLoading = true;
        this._errorMessage = null;

        try {
            const params = new URLSearchParams({ session_id: this._sessionId });
            const response = await fetch(`${API_BASE_URL}/stripe/session?${params.toString()}`, {
                method: 'GET'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.error || 'Unable to load booking details.');
            }

            this._session = data;
            await this.loadListing(data?.metadata?.listingId);
        } catch (error) {
            this._errorMessage = error?.message || 'Unable to load booking details.';
        } finally {
            this._isLoading = false;
        }
    }

    async loadListing(listingId) {
        if (!listingId) return;

        try {
            const params = new URLSearchParams({ listingId });
            const response = await fetch(`${API_BASE_URL}/public/listing?${params.toString()}`, {
                method: 'GET'
            });

            const data = await response.json();

            if (response.ok) {
                this._listing = data?.listing || null;
            }
        } catch (error) {
            console.error('Unable to load listing details for booking success page:', error);
        }
    }

    get metadata() {
        return this._session?.metadata || {};
    }

    get hasSession() {
        return !!this._session;
    }

    get isPaid() {
        return this._session?.payment_status === 'paid';
    }

    get statusText() {
        if (!this._session) return '';

        return this.isPaid ? 'Payment confirmed' : 'Payment processing';
    }

    get pageHeading() {
        return this.isPaid ? 'You are booked' : 'Booking received';
    }

    get supportText() {
        if (this.isPaid) {
            return 'Your booking is confirmed. We are sending your confirmation and notifying your host.';
        }

        return 'Stripe is still processing the payment. Refresh this page in a moment if the status does not update.';
    }

    get propertyName() {
        return this._listing?.property_name || this.metadata.listingPropertyName || 'GetWet reservation';
    }

    get locationLabel() {
        return compactJoin([
            this._listing?.city || this.metadata.listingCity,
            this._listing?.state || this.metadata.listingState,
            this._listing?.country || this.metadata.listingCountry
        ]) || 'Location not provided';
    }

    get addressLabel() {
        const street = this._listing?.street || this.metadata.listingStreet;
        const cityLine = compactJoin([
            this._listing?.city || this.metadata.listingCity,
            this._listing?.state || this.metadata.listingState,
            this._listing?.zip_code || this.metadata.listingZipCode
        ]);

        if (street) {
            return compactJoin([street, cityLine], '\n');
        }

        return this._listing?.formatted_address || this.metadata.listingAddress || 'Address not available';
    }

    get hostLabel() {
        return this._listing?.host_display_name ||
            this._listing?.host_first_name ||
            this.metadata.hostDisplayName ||
            'Your host';
    }

    get hostEmail() {
        return this._listing?.host_email || this.metadata.hostEmail || null;
    }

    get hostPhone() {
        return this._listing?.host_phone || this.metadata.hostPhone || null;
    }

    get dateLabel() {
        return formatDate(this.metadata.date);
    }

    get timeLabel() {
        const labelRange = compactJoin([
            this.metadata.startTimeLabel,
            this.metadata.endTimeLabel
        ], ' - ');

        return labelRange || 'Time not provided';
    }

    get guestCountLabel() {
        const guests = Number(this.metadata.guestCount);

        if (!Number.isFinite(guests) || guests <= 0) {
            return 'Not provided';
        }

        return `${guests} guest${guests === 1 ? '' : 's'}`;
    }

    get amountPaidLabel() {
        return formatCurrencyFromCents(
            this._session?.amount_total || this.metadata.totalPriceCents,
            this._session?.currency || 'USD'
        );
    }

    get confirmationNumber() {
        return this._session?.id || this._sessionId || 'Not available';
    }

    get errorMessage() {
        return this._errorMessage;
    }

    get showLoading() {
        return this._isLoading && !this._session;
    }

    async copyText(value) {
        if (!value) return;

        try {
            await navigator.clipboard.writeText(value);
        } catch (error) {
            const textarea = document.createElement('textarea');
            textarea.value = value;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
    }

    handleCopyAddress(event) {
        this.copyText(this.addressLabel);
        animateCopy(this, event);
    }


    handleCopyHostEmail(event) {
        this.copyText(this.hostEmail);
        animateCopy(this, event);
    }

    handleCopyHostPhone(event) {
        this.copyText(this.hostPhone);
        animateCopy(this, event);
    }

    handleViewListing() {
        const listingId = this.metadata.listingId;
        if (!listingId) return;

        this.dispatchEvent(new CustomEvent('viewlisting', {
            detail: { listingId },
            bubbles: true,
            composed: true
        }));
    }

    handleMessageHost() {
        const listingId = this.metadata.listingId;
        if (!listingId) return;

        this.dispatchEvent(new CustomEvent('messagehost', {
            detail: { listingId },
            bubbles: true,
            composed: true
        }));
    }

    handleBackHome() {
        this.dispatchEvent(new CustomEvent('backtohome', {
            bubbles: true,
            composed: true
        }));
    }
}
